import { FC } from 'react';
import BackIcon from '../../assets/logo/BackIcon';
import Theme from '../../components-library/Theme';
import CustomButton from '../../components-library/buttons/CustomButton';
import { ButtonTypes, CompanyDetailsInterface } from '../../services/exports/Interfaces';
import { Radio } from '@mui/material';
import classnames from 'classnames';
import useScheduledOrderStuff from './useScheduledOrderStuff';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { useTranslation } from 'react-i18next';
import useTheme from '../../hooks/ui/useTheme';

interface Props {
  company: CompanyDetailsInterface;
  className?: string;
}

const ScheduledOrderModal: FC<Props> = ({ company, className }) => {
  const { formatTime } = useAppMethods();
  const { t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:ScheduledOrderModal' });
  const theme = useTheme();
  const {
    onBackClick,
    days,
    orderSlots,
    more,
    isScheduleOrderDisabled,
    isLoading,
    toggleMore,
    onDayClick,
    hasOrderSlots,
    orderSlot,
    onOrderSlotClick,
    onScheduleOrderClick,
  } = useScheduledOrderStuff({
    company,
  });

  const moreLessBtnTitle = t(more ? 'show_less' : 'show_more');

  return (
    <section
      className={classnames(
        'pb-md lg:pb-lg px-md lg:px-lg flex flex-col h-full bg-brand-background-primary z-20',
        className,
      )}
    >
      <div className="flex gap-x-md bg-brand-background-primary py-md">
        <span
          className="rounded-full shadow-icon dark:shadow-none size-6 flex items-center justify-center cursor-pointer"
          onClick={onBackClick}
        >
          <BackIcon className="size-3" color={Theme.color.mobile.tertiary} />
        </span>
        <h3 className="text-md">{t('scheduled_order')}</h3>
      </div>
      <div className="w-full overflow-y-auto space-y-md pt-md">
        <section className="gap-[14px] flex flex-wrap">
          {days?.map((day) => (
            <CustomButton
              key={day.dateLabel}
              className={classnames(
                'flex justify-between !w-[calc(50%-8px)] shadow-none !py-md dark:border-brand-primary/30',
                {
                  'border-black dark:!border-brand-primary': day.active,
                },
              )}
              fontStyle="text-brand-text-secondary"
              buttonType={ButtonTypes.fifth}
              onClick={() => onDayClick(day.date)}
            >
              <p className="text-sm">{day.label}</p>
              <p className="text-sm">{day.dateLabel}</p>
            </CustomButton>
          ))}
        </section>
        <CustomButton
          buttonType={ButtonTypes.fifth}
          fontStyle="text-brand-text-secondary"
          className="shadow-none !py-md"
          onClick={toggleMore}
        >
          <p className="text-sm">{moreLessBtnTitle}</p>
          <BackIcon
            className={classnames('ml-md w-2 h-3', { 'rotate-90': more, '-rotate-90': !more })}
            color={Theme.color.gray['700']}
          />
        </CustomButton>
        <section>
          {hasOrderSlots &&
            orderSlots.map((slot) => (
              <article
                key={slot.start_time}
                className="border-b py-[14px] first:border-t flex gap-x-[22px] items-center cursor-pointer"
                onClick={() => onOrderSlotClick(slot.start_time)}
              >
                <Radio checked={orderSlot === slot.start_time} value={slot.start_time} />
                <p className="text-sm">{formatTime(slot.start_time)}</p>
              </article>
            ))}
        </section>
      </div>
      <CustomButton
        className="mt-auto flex-row-reverse"
        loading={isLoading}
        disabled={isScheduleOrderDisabled}
        onClick={onScheduleOrderClick}
        title={t('schedule_order')}
        customIcon={
          <BackIcon
            className="rotate-180 w-2 h-3 ml-sm"
            color={
              isScheduleOrderDisabled
                ? theme.buttons[ButtonTypes.disabled].text
                : theme.buttons[ButtonTypes.primary].text
            }
          />
        }
      />
    </section>
  );
};

export default ScheduledOrderModal;
